// app/contexts/MenuContext.tsx
'use client'

import { MenuItem } from '@/common/_types/menuTypes'
import { useDispatch, useSelector } from '@/store/hooks'
import { setMenus } from '@/store/menu/menuSlice'
import { AppState } from '@/store/store'
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import { useRequestApi } from './useRequestApi'

interface MenuContextType {
  menus?: MenuItem[]
  excludedTopMenus: String[]
  matchedMenu?: MenuItem
  setMatchedMenuByPathname: (pathname: string) => void
  setCommonMenu: () => void
  setAdmMenu: () => void
  setIsFetchCommonMenu: (isFetchCommonMenu: boolean) => void
}

const MenuContext = createContext<MenuContextType | undefined>(undefined)

export const MenuProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const menus = useSelector((state: AppState) => state.menu.menus)
  const auth = useSelector((state: AppState) => state.auth)
  const dispatch = useDispatch()
  const { sendHttpRequest } = useRequestApi() // Api(fetch) 호출 함수

  const [excludedTopMenus, setExcludedTopMenus] = useState<String[]>([])
  const [matchedMenu, setMatchedMenu] = useState<MenuItem>()
  const [isFetchCommonMenu, setIsFetchCommonMenu] = useState<boolean>(true) //공통메뉴패치여부

  //pathname에 맞는 메뉴 찾기
  const findMatchedMenu = (
    menuList: MenuItem[],
    pathname: string,
    menuNmPaths: string[],
    urlAddrPaths: string[],
    menuTypeCdPaths: string[],
    menuExplnPaths: string[],
  ): MenuItem | undefined => {
    for (let menu of menuList) {
      if (menu.urlAddr === pathname) {
        return {
          ...menu,
          menuNmPaths: [...menuNmPaths, menu.menuNm],
          urlAddrPaths: [...urlAddrPaths, menu.urlAddr ?? ''],
          menuTypeCdPaths: [...menuTypeCdPaths, 'MUT001'],
          menuExplnPaths: [...menuExplnPaths, menu.menuExpln ?? ''],
        }
      }

      if (menu.children && menu.children.length > 0) {
        const found = findMatchedMenu(
          menu.children,
          pathname,
          [...menuNmPaths, menu.menuNm],
          [...urlAddrPaths, menu.urlAddr ?? ''],
          [...menuTypeCdPaths, 'MUT001'],
          [...menuExplnPaths, menu.menuExpln ?? ''],
        )
        if (!!found) return found
      }
    }
    return undefined
  }

  //pathname에 맞는 메뉴 setting
  const setMatchedMenuByPathname = useCallback(
    async (pathname: string) => {
      if (!menus || menus.length === 0) {
        fetchMatchedMenu(pathname)
        return
      }

      let menu = findMatchedMenu(menus, pathname, [], [], [], [])
      if (!!menu) {
        setMatchedMenu(menu)
      } else {
        if (!matchedMenu) {
          fetchMatchedMenu(pathname)
        }
      }
    },
    [menus],
  )

  const fetchMatchedMenu = async (pathname: string) => {
    const response = await sendHttpRequest(
      'GET',
      '/common/menu/matchedMenu?urlAddr=' + pathname,
      null,
      true,
      {},
      false,
    )
    if (response.resultType === 'success') {
      setMatchedMenu(response.data)
    } else {
      alert(response.message)
    }
  }

  //common menu 조회
  const setCommonMenu = useCallback(async () => {
    if (!isFetchCommonMenu) return

    const response = await sendHttpRequest(
      'GET',
      '/common/menu/hierarchy',
      null,
      true,
      {},
      false,
    )

    if (response.resultType === 'success') {
      dispatch(setMenus(response.data.menus))
      setExcludedTopMenus(response.data.excludedTopMenus)
      setIsFetchCommonMenu(false)
    } else {
      alert(response.message)
    }
  }, [auth])

  //adm menu 조회
  const setAdmMenu = useCallback(async () => {
    const response = await sendHttpRequest(
      'GET',
      '/adm/menu/hierarchy',
      null,
      true,
      {},
      false,
    )

    if (response.resultType === 'success') {
      dispatch(setMenus(response.data.menus))
      setExcludedTopMenus(response.data.excludedTopMenus)
    } else {
      alert(response.message)
    }
  }, [auth])

  return (
    <MenuContext.Provider
      value={{
        menus,
        excludedTopMenus,
        matchedMenu,
        setMatchedMenuByPathname,
        setCommonMenu,
        setAdmMenu,
        setIsFetchCommonMenu,
      }}
    >
      {children}
    </MenuContext.Provider>
  )
}

export const useMenu = () => {
  const context = useContext(MenuContext)
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider')
  }
  return context
}
